import type { ParsedUrlQuery } from 'querystring';

import {
    type AlternateLink,
    type ExperimentGroupOrRobots,
    type FullContext,
    type NavItem,
    type Platform,
    type Tag,
} from '@common/clients/api';
import { type ExternalSnippetsByPlacement } from '@common/types/ExternalSnippetsByPlacement';
import { type ModulesBySection } from '@common/types/ModulesBySection';
import { type Route } from '@common/types/Route';
import { type SidebarData } from '@common/types/Sidebar';
import { type ThemeID } from '@common/types/ThemeID';
import { type Host } from '@common/utils/HostUtil';
import { type ResolvedPromiseHolder } from '@common/utils/PromiseHolder';

import { type ApiProps } from './ApiProps';
import { type CaptchaProps } from './CaptchaProps';
import { type CrossPlatformByPlatformID } from './CrossPlatformByPlatformID';
import { type RouteParams } from './RouteParams';

/**
 * Host specific context data.
 */
export interface HostContextData {
    /**
     * Can be used to cache-bust both Next.js and API caches.
     * This property is request specific, but practical to have
     * in `HostContextData` as it used when making API requests.
     */
    cacheBuster?: string;
    context?: FullContext;
    contexts: FullContext[];
    env: Record<string, string>;
    hostname: string;
    host: Host;
    platform: Platform;
    config: {
        api: ApiProps;
        socket: ApiProps;
        crossPlatform: CrossPlatformByPlatformID;
        captchaConfig: CaptchaProps;
    };
    topLevelCategories: boolean;
    topLevelPages: boolean;
}

/**
 * Request specific context data.
 */
export interface ContextData extends HostContextData {
    adsTempDisabled?: boolean;
    allowAds: boolean | 'preview';
    allowExternalSnippets: boolean | 'preview';
    allowBetting: boolean | 'preview' | Promise<boolean>;
    allowPremium: boolean;
    allowMyPXR: boolean;
    allowTracking: boolean;
    alternateLinks?: AlternateLink[] | Promise<AlternateLink[]>;
    context: FullContext;
    experimentGroup?: ExperimentGroupOrRobots;
    externalSnippets?: ExternalSnippetsByPlacement | Promise<ExternalSnippetsByPlacement | undefined>;
    isMobileApp?: boolean;
    isPlatformContext: boolean;
    modulesBySection?: ModulesBySection | Promise<ModulesBySection | undefined>;
    route?: Route;
    query?: ParsedUrlQuery;
    path?: string;
    params?: RouteParams;
    sidebarProps?: SidebarData | Promise<SidebarData | undefined>;
    navItems?: NavItem[] | Promise<NavItem[]>;
    premiumSubscriptionTag?: number;
    themeID: ThemeID;
    trendingTags?: Tag[] | Promise<Tag[]>;
    now: number;
}

export type ResolvedContextData = ResolvedPromiseHolder<ContextData>;

export const isContextData = (x: ContextData | any): x is ContextData => !!x?.config?.api?.host;
